import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useEffect, useRef, useState } from "react";
import { ScrollView } from "react-native";
import { useFragment } from "react-relay";

import {
  getLastSelectedBookingSelection,
  getStoredCourseIdsForFacility,
  setLastSelectedBookingSelection,
} from "@/utils/bookingSelectionStorage";
import { ChevronDownIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Menu } from "@whoosh/design-system/src/components/overlay/Menu";
import { VStack, Box } from "@whoosh/design-system/src/components/primitive";

import { ScheduleSwitcherOption } from "./ScheduleSwitcherOption";
import { ScheduleSwitcher_session$key } from "./__generated__/ScheduleSwitcher_session.graphql";
import {
  ScheduleOption,
  useGetScheduleSwitcherOptions,
} from "./hooks/useGetScheduleSwitcherOptions";

type Props = {
  sessionRef: ScheduleSwitcher_session$key | null;
  onChange: (option: ScheduleOption) => void;
  selectedOption: ScheduleOption | null;
  syncSelectedOptionWithStoredDefault?: boolean;
};

export const ScheduleSwitcher = ({
  onChange,
  selectedOption,
  sessionRef,
  syncSelectedOptionWithStoredDefault = false,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const defaultSelectionRequestId = useRef(0);

  const session = useFragment(ScheduleSwitcherFragmentString, sessionRef);

  const options = useGetScheduleSwitcherOptions(session ?? null);
  const singleFacilityClub = options?.length === 1;

  useEffect(() => {
    defaultSelectionRequestId.current += 1;
    const requestId = defaultSelectionRequestId.current;

    if (
      options.length === 0 ||
      (selectedOption && !syncSelectedOptionWithStoredDefault)
    ) {
      return;
    }

    const selectDefaultOption = async () => {
      const storedOption = await getStoredDefaultOption(options);
      const optionToSelect =
        storedOption ?? (selectedOption ? null : options[0]);

      if (!optionToSelect) {
        return;
      }

      if (
        selectedOption?.type === optionToSelect.type &&
        selectedOption?.id === optionToSelect.id
      ) {
        return;
      }

      if (defaultSelectionRequestId.current === requestId) {
        onChange(optionToSelect);
      }
    };

    void selectDefaultOption();
  }, [onChange, options, selectedOption, syncSelectedOptionWithStoredDefault]);

  const handleOptionChange = (option: ScheduleOption) => {
    const facilityId =
      option.facilityIds?.[0] ??
      (option.type === "facility" ? option.id : null);

    if (facilityId) {
      setLastSelectedBookingSelection({ facilityId });
    }

    onChange(option);
  };

  return (
    <VStack gap={4} testID="facility-selector-container">
      <Menu
        overrideIsOpen={isOpen}
        onClose={() => setIsOpen(false)}
        trigger={() =>
          selectedOption ? (
            <Box bg="surface-primary" borderRadius="2">
              <ScheduleSwitcherOption
                selected
                onPress={() => !singleFacilityClub && setIsOpen(!isOpen)}
                option={selectedOption}
                icon={!singleFacilityClub ? <ChevronDownIcon /> : undefined}
                isOpen={isOpen}
                testID="facility-selector-trigger"
                accessibilityLabel={t`Select a different facility`}
              />
            </Box>
          ) : (
            <></>
          )
        }
        content={() => (
          <Menu.Content size="matchTrigger">
            <ScrollView>
              <VStack
                backgroundColor="surface-primary"
                maxHeight={{ base: "auto", lg: 320 }}
                gap={2}
              >
                {options.map((option) =>
                  option.id === selectedOption?.id ? null : (
                    <ScheduleSwitcherOption
                      key={option.id}
                      onPress={(o) => {
                        handleOptionChange(o);
                        setIsOpen(false);
                      }}
                      option={option}
                      accessibilityLabel={t`Select the ${option?.label} facility`}
                    />
                  )
                )}
              </VStack>
            </ScrollView>
          </Menu.Content>
        )}
      />
    </VStack>
  );
};

const getStoredDefaultOption = async (
  options: ScheduleOption[]
): Promise<ScheduleOption | null> => {
  const { facilityId } = await getLastSelectedBookingSelection();
  const commonStoredOption = findOptionForStoredSelection(options, {
    facilityId,
  });

  if (commonStoredOption) {
    return commonStoredOption;
  }

  for (const option of options) {
    const facilityIds = option.facilityIds ?? [];

    for (const optionFacilityId of facilityIds) {
      const storedCourseIds = await getStoredCourseIdsForFacility(
        optionFacilityId
      );

      if (storedCourseIds.length) {
        return option;
      }
    }
  }

  return null;
};

const findOptionForStoredSelection = (
  options: ScheduleOption[],
  {
    facilityId,
  }: {
    facilityId: string | null;
  }
) => {
  if (facilityId) {
    return (
      options.find((option) => option.facilityIds?.includes(facilityId)) ?? null
    );
  }

  return null;
};

const ScheduleSwitcherFragmentString = graphql`
  fragment ScheduleSwitcher_session on Session {
    ...useGetScheduleSwitcherOptionsFragment_session
  }
`;
